/* noted. — Skipstone experience layer, v7.7.
   Header "catch": pinned and transparent over the hero for the first CATCH px
   of scroll; past that the page catches it and carries it off in EXACT
   lockstep with the scroll. No independent animation, no mid-page reveal, no
   background change — the header only exists near the top, always
   transparent. Scrolling back up re-delivers it in reverse.
   v7.2: NO requestAnimationFrame — rAF stalls in occluded windows and a
   pending frame wedges any ticking flag (learned the hard way in round 4).
   The per-scroll work is a few reads and one transform write; it runs
   directly in the passive scroll handler. Node re-queried every call so a
   section re-render can't strand the transform on a detached node.
   v7.3: also engages on pages WITHOUT a hero when the brand snippet marks
   the document (data-skipstone-clear="1") — e.g. the Wines page, which
   opens straight on the Papyrus flourish block pulled up under the header.

   v7.4 (R25): THE SCROLLED STATE. Everything above is unchanged — the catch,
   the lockstep carry, the tone probe. What is new is what happens AFTER the
   header has been carried off.

   Until now there was no exit: html.skipstone-header-scrolled was removed once
   at init and never added by anything, and the header simply stayed gone. It
   now returns as a translucent slate surface and stays there until the visitor
   is back near the top.

   TWO THRESHOLDS, NOT ONE, and the hysteresis is the whole point. Toggling on a
   single boundary would flap: the class removes the inline transform, which
   moves the header, which changes nothing about scrollY but does mean the
   boundary is crossed in both directions by a one-pixel wheel movement. So:

     ENTER  y >= CATCH + headerHeight + 12   the carry is complete, the header
                                             is off screen; hand it to CSS
     EXIT   y <= CATCH                       back inside the free-scroll band,
                                             where carried is 0 anyway

   Between the two the state is sticky in both senses. Going down, the header
   slides back in over --sk-dur-nav. Going up, it stays a slate bar until the
   page is near the top and then fades back to transparent. Neither direction
   crosses a discontinuity: at EXIT the lockstep transform is already 0, so
   handing control back to the inline style moves nothing.

   WHILE SCROLLED THE INLINE TRANSFORM IS CLEARED. An inline style beats a class
   rule, so CSS cannot animate the header back in while the JS is still writing
   translate3d on every scroll event. The JS stands down; skipstone-brand's
   transform rule and its transition take over. */
(function () {
  var CATCH = 140; // free scroll before the page catches the header

  /* ---------- v7.5 (R55) — THE SCROLLED HEADER IS A DRAWER ON THE SCROLL ----
     Everything up to the carry is unchanged: transparent over the hero,
     caught at CATCH and carried off in lockstep. What changes is what
     happens once the brown header has become the active header.

     v7.4 handed the transform to CSS at that point and animated the whole
     bar back in over --sk-dur-nav — a triggered show/hide. It is now a
     drawer whose position is the running sum of the visitor's own scroll:

         hidden = clamp(hidden + deltaY, 0, headerHeight)
         transform: translate3d(0, -hidden, 0)

     Scroll down 10px and it moves 10px up; scroll up 15px and 15px of it
     comes back; reverse at any point and it reverses from exactly there. No
     snapping, no minimum distance, no hysteresis, no transition on the
     transform — the write happens in the passive scroll handler, one style
     per event, so the bar tracks a trackpad or a thumb with nothing between
     the gesture and the pixel. The header height is read once and re-read
     only on resize, so a phone header is its own height and nothing is
     hardcoded.

     THE HERO THRESHOLD IS THE HERO'S OWN EDGE. skipstone-header-scrolled
     goes on when the first section's bottom passes the top of the viewport
     and comes off when it returns, so the two visual states never stack:
     the transparent header is the carry's business, the brown one the
     drawer's, and the hand-over is at one boundary read from the page. On a
     page with no hero (Wines, the collector pages) the old CATCH+height
     threshold stands.

     ON ENTERING THE DRAWER STATE the bar starts fully hidden — it has just
     been carried off — so nothing jumps; the first upward scroll pulls it
     down from -h. On LEAVING (back into the hero) hidden resets to 0 and the
     carry's transform, which is 0 at that point, takes over: no
     discontinuity in either direction.

     SUSPENDED WHILE THE VISITOR IS IN THE HEADER. With the About menu, the
     drawer, the cart or search open, hidden is pinned to 0 and scroll deltas
     are ignored, so the controls do not move away from a hand that is on
     them; when the overlay closes the drawer resumes from 0 and the next
     gesture moves it. The check is a :has() on the header, no listeners.

     STILL NO requestAnimationFrame — the house rule. A frame that never
     arrives in an occluded tab would leave the bar wherever it was; a
     synchronous write cannot. */

  function init() {
    var main = document.getElementById('main');
    var sec = document.querySelector('.shopify-section--header');
    if (!main || !sec) return;
    var marked = document.documentElement.getAttribute('data-skipstone-clear') === '1';
    var hero = main.querySelector('[allow-transparent-header]');
    if (!hero && !marked) return;

    var root = document.documentElement;
    root.classList.add('skipstone-clear-header');
    /* R66 (Round 49, item 6) — a hero can ask the header to carry its fill
       over it (data-sk-header-filled, from the section's header_fill
       setting). Everything else is identical: the header is still laid over
       the photograph, the catch and the drawer run unchanged, only the
       surface is on from y = 0 and the tone probe is skipped because the
       nav is white on the surface. No page is named anywhere. */
    var filled = !!(hero && hero.hasAttribute('data-sk-header-filled'));
    root.classList.toggle('skipstone-header-filled', filled);
    root.classList.remove('skipstone-header-scrolled');

    var heroSection = hero ? hero.closest('.shopify-section') : null;
    var h = sec.offsetHeight || 0;
    var heroBottom = heroSection ? heroSection.getBoundingClientRect().bottom + window.scrollY : 0;
    var lastY = Math.max(0, window.scrollY || 0);
    var hidden = 0;
    var held = false; /* R77 (Round 50, item 14) — see the landing handler */

    function measure() {
      var s = document.querySelector('.shopify-section--header');
      h = s ? (s.offsetHeight || 0) : 0;
      heroBottom = heroSection && main.contains(heroSection)
        ? heroSection.getBoundingClientRect().bottom + window.scrollY : 0;
    }

    function engaged() {
      var s = document.querySelector('.shopify-section--header');
      if (!s) return false;
      try {
        return !!s.querySelector('details[open], [open], [aria-expanded="true"]');
      } catch (e) { return false; }
    }

    function update() {
      var s = document.querySelector('.shopify-section--header');
      if (!s) return;
      var y = Math.max(0, window.scrollY || 0);
      var dy = y - lastY;
      lastY = y;
      if (!h) measure();

      /* ---------- v7.6 (R59) — ONE POSITION, CONTINUOUS AT THE HERO EDGE ----
         v7.5 kept two positions: the carry's, a function of y (y - CATCH,
         so it re-asserted -98 the instant the state flipped back), and the
         drawer's running sum. Crossing the hero edge upward with the bar
         showing therefore snapped it from 0 to -98 in one pixel, and it
         stayed hidden until y = 242. Measured, Round 45.

         Now there is one `hidden`, driven by the scroll delta in both
         states. In the hero band it is CAPPED by the carry — it can never
         be more hidden than y - CATCH allows — so scrolling down from the
         top is still exact lockstep (0 until 140, then pixel for pixel), and
         scrolling up toward the top the cap brings the bar home by 242 at
         the latest. Crossing the edge in either direction changes only the
         cap, never the position, so the bar cannot jump on a state flip.
         The v7.4 +12 overshoot is gone; the bar hides at h, its own height. */
      var cap = heroSection ? Math.min(Math.max(y - CATCH, 0), h) : h;
      var was = root.classList.contains('skipstone-header-scrolled');
      var now = heroSection
        ? (y >= heroBottom - h)                       /* the hero's own edge */
        : (was ? (y > CATCH) : (y >= CATCH + h + 12)); /* no hero: v7.4 rule */
      if (now !== was) root.classList.toggle('skipstone-header-scrolled', now);

      if (held) {
        hidden = engaged() ? 0 : Math.min(Math.max(hidden + dy, 0), h);
        if (now || hidden <= cap) { if (dy < 0 || hidden <= cap) held = now ? (dy >= 0) : false; }
      } else if (now) {
        hidden = engaged() ? 0 : Math.min(Math.max(hidden + dy, 0), h);
      } else {
        hidden = Math.min(Math.max(hidden + dy, 0), cap);
      }
      var t = hidden > 0 ? 'translate3d(0, ' + -hidden + 'px, 0)' : '';
      if (s.style.transform !== t) s.style.transform = t;

      if (!now && hidden < h && !filled) {
        var probeY = h * 0.6;
        var sections = main.querySelectorAll('.shopify-section');
        var tone = '68 68 68';
        for (var i = 0; i < sections.length; i++) {
          var r = sections[i].getBoundingClientRect();
          if (r.top <= probeY && r.bottom > probeY) {
            if (sections[i].querySelector('.color-scheme--with-image-overlay, .content-over-media')) {
              tone = '255 255 255';
            }
            break;
          }
        }
        document.documentElement.style.setProperty('--skipstone-header-rgb', tone);
      }
    }

    /* A landing made by skipstone-anchors (a menu click, an arrival on a
       section) is not a gesture: the bar is shown in full and the visitor's
       next real scroll moves it from there. */
    document.addEventListener('skipstone:landing', function () {
      lastY = Math.max(0, window.scrollY || 0);
      /* Settle the state here rather than on the scroll event that follows:
         entering the drawer state normally starts the bar fully hidden
         (it has just been carried off), and that reset would undo this. */
      var s = document.querySelector('.shopify-section--header');
      if (!h) measure();
      var now = heroSection ? (lastY >= heroBottom - h) : (lastY >= CATCH + h + 12);
      root.classList.toggle('skipstone-header-scrolled', now);
      /* R102 (Round 56, Rule 3) — DELIBERATE REVERSAL of R77. A declared
         landing arrives with the bar PRESENT, in full, and the panel sits one
         universal padding under it (the landing's own offset, in
         skipstone-anchors). The visitor's next real scroll moves it from
         there through the normal carry. */
      hidden = 0;
      held = false;
      if (s) s.style.transform = 'translate3d(0, 0, 0)';
    });

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', function () { measure(); update(); });
    document.addEventListener('visibilitychange', update);
    /* An overlay closing does not scroll; re-run so a drawer pinned at 0
       does not wait for the next gesture to be told it may move again. */
    document.addEventListener('toggle', update, true);
    document.addEventListener('click', function () { setTimeout(update, 0); }, true);
    setInterval(function () { measure(); update(); }, 800); // keeps state honest if an event is ever swallowed
    measure();
    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
